// «Passer etter»-forslag: åpne oppdrag sjåføren rekker etter et annet oppdrag,
// med lite tomkjøring. Kjøretid er et anslag (luftlinje × veifaktor ved snittfart)
// — kan byttes mot ekte kjøretid senere uten å endre kallerne.
import { haversineKm } from './priceCalculator';
import { toDate } from './orderCardFormat';

export const MATCH_CONFIG = {
  roadFactor: 1.3,  // luftlinje → veiavstand
  avgKmh:     50,   // snittfart lastebil
  loadMin:    15,
  unloadMin:  15,
  lateMin:    30,   // maks forsinkelse på levering
  earlyMin:   120,  // maks tidlig levering
  maxEmptyKm: 20,
  maxWaitMin: 240,
};

const hasCoords = (a) => Number.isFinite(a?.lat) && Number.isFinite(a?.lng);

// Best passform først: minst tomkjøring, så minst ventetid
const byFit = (a, b) => a.emptyKm - b.emptyKm || a.waitMin - b.waitMin;

function roadKm(from, to, config) {
  return haversineKm(from.lat, from.lng, to.lat, to.lng) * config.roadFactor;
}

function driveMin(km, config) {
  return (km / config.avgKmh) * 60;
}

function minutes(value) {
  const date = toDate(value);
  return date ? date.getTime() / 60000 : null;
}

function isRoutable(order) {
  return hasCoords(order?.pickupAddress) && hasCoords(order?.deliveryAddress) && minutes(order.scheduledAt) != null;
}

export function matchesAfter(anchor, candidates, config = MATCH_CONFIG) {
  if (!isRoutable(anchor)) return [];
  const anchorAt = minutes(anchor.scheduledAt);
  const doneA = anchorAt + config.unloadMin;

  const matches = [];
  for (const order of candidates) {
    if (order.id === anchor.id || !isRoutable(order)) continue;
    const orderAt = minutes(order.scheduledAt);
    if (orderAt <= anchorAt) continue;

    const emptyKm = roadKm(anchor.deliveryAddress, order.pickupAddress, config);
    if (emptyKm > config.maxEmptyKm) continue;

    const loadedKm = roadKm(order.pickupAddress, order.deliveryAddress, config);
    const arrive = doneA + driveMin(emptyKm, config) + config.loadMin + driveMin(loadedKm, config);

    // For sent: sjåføren kan ta inn tid ved å levere ankeret tidligere, men maks earlyMin
    const overrun = arrive - (orderAt + config.lateMin);
    if (overrun > config.earlyMin) continue;

    // For tidlig: levering kan skje inntil earlyMin før ønsket tid, først da blir det venting
    const waitMin = Math.max(0, orderAt - config.earlyMin - arrive);
    if (waitMin > config.maxWaitMin) continue;

    matches.push({ order, emptyKm, waitMin });
  }

  return matches.sort(byFit);
}

// For merket i lista: hvilket av sjåførens oppdrag passer hvert åpent oppdrag best etter?
export function bestAnchorFor(candidates, anchors, config = MATCH_CONFIG) {
  const best = new Map();
  for (const anchor of anchors) {
    for (const { order, emptyKm, waitMin } of matchesAfter(anchor, candidates, config)) {
      const current = best.get(order.id);
      if (!current || byFit({ emptyKm, waitMin }, current) < 0) {
        best.set(order.id, { anchor, emptyKm, waitMin });
      }
    }
  }
  return best;
}

// For «Mine oppdrag»: det ene beste oppdraget som passer etter, samme dag som ankeret.
export function bestMatchSameDay(anchor, candidates, config = MATCH_CONFIG) {
  const day = toDate(anchor.scheduledAt)?.toDateString();
  return matchesAfter(anchor, candidates, config)
    .find(m => toDate(m.order.scheduledAt).toDateString() === day) ?? null;
}
